import { useState, useCallback, useContext, createContext, useMemo } from 'react';
import React from 'react';

const STORAGE_KEY = 'color-favorites';
const MAX_FAVORITES = 16;

function readFromStorage(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Validate: only keep uppercase 7-char hex strings (#RRGGBB)
    return parsed.filter(
      (c): c is string => typeof c === 'string' && /^#[0-9A-F]{6}$/.test(c),
    );
  } catch {
    return [];
  }
}

function writeToStorage(favorites: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
  } catch {
    // Silently ignore storage errors (private browsing, quota exceeded, etc.)
  }
}

interface FavoriteColorsContextType {
  favorites: string[];
  addFavorite: (hex: string) => void;
  removeFavorite: (hex: string) => void;
  isFavorite: (hex: string) => boolean;
}

const FavoriteColorsContext = createContext<FavoriteColorsContextType>({
  favorites: [],
  addFavorite: () => {},
  removeFavorite: () => {},
  isFavorite: () => false,
});

export function FavoriteColorsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [favorites, setFavorites] = useState<string[]>(() =>
    readFromStorage(),
  );

  const addFavorite = useCallback((hex: string) => {
    const normalized = hex.toUpperCase();
    setFavorites((prev) => {
      if (prev.includes(normalized)) return prev;
      if (prev.length >= MAX_FAVORITES) return prev;
      const next = [...prev, normalized];
      writeToStorage(next);
      return next;
    });
  }, []);

  const removeFavorite = useCallback((hex: string) => {
    const normalized = hex.toUpperCase();
    setFavorites((prev) => {
      const next = prev.filter((c) => c !== normalized);
      writeToStorage(next);
      return next;
    });
  }, []);

  const isFavorite = useCallback(
    (hex: string) => favorites.includes(hex.toUpperCase()),
    [favorites],
  );

  const value = useMemo(
    () => ({ favorites, addFavorite, removeFavorite, isFavorite }),
    [favorites, addFavorite, removeFavorite, isFavorite],
  );

  return (
    <FavoriteColorsContext.Provider value={value}>
      {children}
    </FavoriteColorsContext.Provider>
  );
}

export function useFavoriteColors() {
  return useContext(FavoriteColorsContext);
}
